import { cellCenter } from '../grid.ts';
import { idiv, imax, mulBp, withinRange } from '../math/fixed.ts';
import { rngInt, rngRange } from '../math/rng.ts';
import type { BattleState, CamsRules, CamsState, Hero, SendaNpc } from '../types.ts';
import { ev, spawnEnemy } from './combat.ts';
import { orderMove } from './heroes.ts';
import { CAM_FACES } from './corazon-layout.ts';
import { pulseLand } from './brasa.ts';
import { tenazaFresh, tenazaGrips, tenazaTick } from './tenaza.ts';
import { shootTick } from './workshop.ts';

/**
 * The trip-hammer's cams (docs/sendas/artifice.md, chapter 3, act 3, the test; §4, puzzle 3, «Las levas del
 * martinete»; the ground in battle/corazon-layout.ts). The heart stone sits on a base that turns with the hammer's
 * shaft (a turn of the shaft, a turn of the base); the base has six faces cut and numbered, 1 to 6. The shaft has six
 * places for cams, with the same numbers: a cam on 4 drops the hammer as face 4 goes under it. Three red veins over the
 * stone tell the order: which face takes the first blow, the second, the third (and a fourth, some seeds). The lever
 * lets the hammer go for one turn only: the faces go by in their order (1, 2, 3…), one every `face`, and the hammer
 * strikes each one with a cam on its place. A face struck out of its turn chips (a piece of the stone off: its mark
 * stays); `chips` of them and the stone is no good: lost. So the order is split into runs that go the shaft's way:
 * 4, 1, 5 is a turn with the cam on 4, then a turn with cams on 1 and 5. Three cams; a cam is put or taken standing
 * `stand` by its place (3 s), only while the shaft is still.
 *
 * A tap on a cam, a place or the lever sends the hero: standing still `stand` by it, what is in its hands and what is
 * there change places (`camOrder`); the lever is pulled in `pull`. La Tenaza (battle/tenaza.ts) comes out of the river
 * for the hammer's wheel; gripping it, the shaft is still: no turn starts, and a turn under way stops where it is till
 * it lets go. The magma's pulses (one on the hero, one on the shaft: working a cam is standing on it) and ash off the
 * river. Won with the stone off the rock and La Tenaza down (either first); lost with the stone chipped `chips` times,
 * Dunna or the hero down.
 *
 * Cost: the shaft is a counter; the strikes are worked out as each face goes by (six a turn); the crab's.
 */

/** The lever's mark (after the six places and the cams' lying spots). */
export const CAM_LEVER = CAM_FACES + 3;

const fresh = (r: CamsRules): CamsState => ({
  on: r.lie.map((_, k) => CAM_FACES + k),
  hand: -1,
  job: -1,
  held: 0,
  turn: -1,
  done: 0,
  chips: 0,
  marks: [0, 0, 0, 0, 0, 0],
  turns: 0,
  freed: -1,
  pulse: r.pulse.first,
  spots: [],
  foe: r.foes.first,
  tz: tenazaFresh(r),
  shot: 0,
});

/** A mark's cell: a place on the shaft (0–5), a cam's lying spot (6 + its index), the lever. */
export const camCell = (r: CamsRules, at: number): number => (at < CAM_FACES ? (r.slots[at] as number) : at < CAM_LEVER ? (r.lie[at - CAM_FACES] as number) : r.lever);

/** The cam at a mark (−1: none). */
export const camAt = (w: CamsState, at: number): number => w.on.indexOf(at);

/** The places with a cam (faces 0 to 5). */
export const camSet = (w: CamsState): number[] => w.on.filter((at) => at >= 0 && at < CAM_FACES).sort((a, b) => a - b);

/** The face under the hammer now in a turn (−1: still), and how far into its pass (0 to `face`). */
export const camFace = (r: CamsRules, w: CamsState): { face: number; k: number } => (w.turn < 0 ? { face: -1, k: 0 } : { face: idiv(w.turn, r.face) % CAM_FACES, k: w.turn % r.face });

/** The hero sent to a mark (a tap on it): a cam, a place, the lever. */
export const camOrder = (s: BattleState, h: Hero, at: number): void => {
  const r = s.rules?.senda?.cams;
  const w = s.senda?.cams;
  if (!r || !w || w.freed >= 0 || at < 0 || at > CAM_LEVER) return;
  w.job = at;
  w.held = 0;
  const c = cellCenter(s.map.radius, camCell(r, at));
  orderMove(s, h, c.x, c.y);
  h.target = 0;
  h.forced = false;
};

/** The hero's hands at the mark: standing still by it, what is in them and what is there change places (or the lever). */
const heroWork = (s: BattleState, r: CamsRules, w: CamsState, cue: (c: string) => void): void => {
  const h = s.heroes.find((x) => x.downTicks === 0);
  if (!h || w.job < 0) return;
  const c = cellCenter(s.map.radius, camCell(r, w.job));
  if (h.moving || !withinRange(h.x, h.y, c.x, c.y, r.reach)) {
    w.held = 0;
    if (!h.moving) w.job = -1;
    return;
  }
  const lever = w.job === CAM_LEVER;
  // The shaft turning: the cams can't be moved till it stops.
  if (w.turn >= 0) {
    w.held = 0;
    if (lever) w.job = -1;
    return;
  }
  if (++w.held < (lever ? r.pull : r.stand)) return;
  w.held = 0;
  if (lever) {
    w.job = -1;
    if (tenazaGrips(s)) {
      ev(s, { t: 'senda', kind: 'leverHeld', npc: '', x: c.x, y: c.y });
      cue('grip');
      return;
    }
    w.turn = 0;
    w.turns++;
    ev(s, { t: 'senda', kind: 'camTurn', npc: '', x: c.x, y: c.y, n: w.turns });
    cue('turning');
    return;
  }
  const at = w.job;
  const there = camAt(w, at);
  const hand = w.hand;
  w.job = -1;
  if (there < 0 && hand < 0) return;
  if (there >= 0) {
    w.on[there] = -1;
    ev(s, { t: 'senda', kind: 'camTake', npc: '', x: c.x, y: c.y, n: at });
  }
  if (hand >= 0) {
    w.on[hand] = at;
    ev(s, { t: 'senda', kind: 'camPut', npc: '', x: c.x, y: c.y, n: at });
  }
  w.hand = there;
};

/** The hammer on a face as it goes by: right in its turn, or a chip. */
const strike = (s: BattleState, r: CamsRules, w: CamsState, face: number, cue: (c: string) => void): void => {
  const want = r.seq[w.done];
  if (face === want) {
    w.done++;
    w.marks[face] = 1;
    ev(s, { t: 'senda', kind: 'strike', npc: '', x: r.stone.x, y: r.stone.y, n: face });
    cue(`right${w.done}`);
    if (w.done >= r.seq.length) {
      w.freed = s.tick;
      w.turn = -1;
      // What was in the hands goes down (the stone is off).
      w.hand = -1;
      w.job = -1;
      ev(s, { t: 'senda', kind: 'freed', npc: 'dunna', x: r.stone.x, y: r.stone.y });
      cue('free');
    }
    return;
  }
  w.chips++;
  w.marks[face] = 2;
  ev(s, { t: 'senda', kind: 'chip', npc: '', x: r.stone.x, y: r.stone.y, n: face });
  cue(`chip${w.chips}`);
};

/** The shaft's turn: a face under the hammer every `face`, struck at the middle of its pass if its place has a cam. */
const turnTick = (s: BattleState, r: CamsRules, w: CamsState, cue: (c: string) => void): void => {
  if (w.turn < 0 || w.freed >= 0 || tenazaGrips(s)) return;
  w.turn++;
  const half = idiv(r.face, 2);
  if (w.turn % r.face === half) {
    const face = idiv(w.turn, r.face);
    ev(s, { t: 'senda', kind: 'face', npc: '', x: r.stone.x, y: r.stone.y, n: face });
    if (face < CAM_FACES && camSet(w).includes(face)) strike(s, r, w, face, cue);
  }
  if (w.turn >= CAM_FACES * r.face && w.freed < 0) {
    w.turn = -1;
    ev(s, { t: 'senda', kind: 'camStill', npc: '', x: r.stone.x, y: r.stone.y });
  }
};

/** The magma's pulse: one on the cam the hero is working (else on the hero), one on a place of the shaft. */
const pulse = (s: BattleState, r: CamsRules, w: CamsState): void => {
  if (s.waveT < w.pulse) return;
  w.pulse = s.waveT + r.pulse.every;
  const t = s.tick + r.pulse.warn;
  const h = s.heroes.find((x) => x.downTicks === 0);
  const add = (x: number, y: number): void => {
    w.spots.push({ x, y, t });
    ev(s, { t: 'senda', kind: 'pulseWarn', npc: '', x, y, n: r.pulse.warn });
  };
  // On the cam the hero is working (standing at it is the price of the puzzle), else on the hero; and one on the shaft.
  const job = w.job >= 0 && w.job !== CAM_LEVER ? cellCenter(s.map.radius, camCell(r, w.job)) : null;
  if (job) add(job.x + rngRange(s.rng, -200, 200), job.y + rngRange(s.rng, -200, 200));
  else if (h) add(h.x + rngRange(s.rng, -300, 300), h.y + rngRange(s.rng, -300, 300));
  if (w.freed < 0) {
    const c = cellCenter(s.map.radius, r.slots[rngInt(s.rng, CAM_FACES)] as number);
    const first = w.spots[w.spots.length - 1];
    if (!first || first.t !== t || !withinRange(first.x, first.y, c.x, c.y, r.pulse.r * 2)) add(c.x, c.y + 300);
  }
};

/** Ash nightmares off the river, now and then. */
const foes = (s: BattleState, r: CamsRules, w: CamsState): void => {
  if (s.waveT < w.foe) return;
  w.foe = s.waveT + r.foes.every;
  const at = r.river[rngInt(s.rng, r.river.length)] as { x: number; y: number };
  for (let k = 0; k < r.foes.n; k++) {
    const e = spawnEnemy(s, 'wraith', 1, false, 0, at.x + rngRange(s.rng, -300, 300), at.y + rngRange(s.rng, -200, 200));
    if (s.rules?.enemyHpBp) e.hp = e.maxHp = imax(1, mulBp(e.maxHp, s.rules.enemyHpBp));
  }
  ev(s, { t: 'senda', kind: 'holeOut', npc: '', x: at.x, y: at.y, n: r.foes.n });
};

export const camsTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = s.rules?.senda?.cams;
  const st = s.senda;
  if (!r || !st) return;
  const w = (st.cams ??= fresh(r));
  if (!s.started) return;
  tenazaTick(s, r, w.tz, cue);
  if (w.freed < 0) heroWork(s, r, w, cue);
  turnTick(s, r, w, cue);
  pulse(s, r, w);
  pulseLand(s, w.spots, r.pulse);
  foes(s, r, w);
  const d = st.npcs.find((n): n is SendaNpc => n.id === 'dunna' && n.hp > 0);
  if (d) {
    if (d.flinch > 0) d.flinch--;
    w.shot = shootTick(s, d, r.dunna, w.shot);
  }
};

/** The stone off the rock, La Tenaza down: two steps. */
export const camsGoal = (s: BattleState): { done: number; need: number } => {
  const w = s.senda?.cams;
  if (!w) return { done: 0, need: 2 };
  return { done: (w.freed >= 0 ? 1 : 0) + (w.tz.down >= 0 ? 1 : 0), need: 2 };
};

/** Won with the stone off and La Tenaza down (a moment after); lost with the stone chipped too often. */
export const camsResult = (s: BattleState): 'won' | 'lost' | null => {
  const r = s.rules?.senda?.cams;
  const w = s.senda?.cams;
  if (!r || !w) return null;
  if (w.chips >= r.chips) return 'lost';
  if (w.freed < 0 || w.tz.down < 0) return null;
  return s.tick >= (w.freed > w.tz.down ? w.freed : w.tz.down) + 24 ? 'won' : null;
};
